import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {chakra, useSlotRecipe} from '@chakra-ui/react3'
import {hamburgerIconRecipe} from './hamburger-icon.recipe'
import {BARS, DIAGONALS} from './hamburger-icon.geometry'

const Svg = chakra('svg')
const Line = chakra('line')

/**
 * Animated hamburger ⇄ X glyph for the Xleap mobile menu toggle (STOK-17031
 * Step 11).
 *
 * Purely presentational (`aria-hidden`) — it never carries its own accessible
 * name. The toggle's accessible name (`header.menu.open` / `header.menu.close`)
 * lives on the enclosing `HeaderIconButton` in `navigation/header/index.jsx`;
 * this component only renders the visual glyph.
 *
 * The five `<line>`s stay mounted in both resting states so the recipe's
 * transitions always have a painted start value to animate from. `open` is
 * mirrored onto each as `data-state="open"|"closed"`.
 *
 * All motion CSS — the staged two-phase transition, the `non-scaling-stroke`
 * bloom, and the resting colour — lives in `hamburger-icon.recipe.js`; this
 * component owns no styling.
 *
 * `className="icon"` mirrors the marker already used by `MultiColorIcon`
 * (`app/leap/components/icons/index.jsx`) so that, when nested inside a
 * `HeaderIconButton`, the button's `_icon` selector sizes this glyph the same
 * way it sizes the cart/account SVG icons.
 *
 * @component
 * @param {boolean} [props.open=false] - Whether the nav is open; reflected as
 *   `data-state="open"|"closed"` on the glyph and drives the morph.
 * @returns {JSX.Element}
 */
const HamburgerIcon = ({open = false}) => {
    const recipe = useSlotRecipe({recipe: hamburgerIconRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const state = open ? 'open' : 'closed'

    return (
        <Svg
            className="icon"
            aria-hidden="true"
            // Exception (docs/agents/review-testing.md): this glyph is
            // presentational (aria-hidden, no accessible name by design), so it
            // has no ARIA role a test could target — data-testid is the only way
            // to assert its state in a behavioral (non-visual) test.
            data-testid="hamburger-icon"
            data-state={state}
            css={styles.root}
            focusable="false"
            viewBox="0 0 24 24"
        >
            {[...BARS, ...DIAGONALS].map(({slot, x1, y1, x2, y2}) => (
                <Line
                    key={slot}
                    css={styles[slot]}
                    data-state={state}
                    // Keep the stroke a constant round-capped width while the
                    // recipe grows the diagonals via `scale` (see
                    // hamburger-icon.recipe.js), so the bloom never distorts it.
                    vectorEffect="non-scaling-stroke"
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                />
            ))}
        </Svg>
    )
}

HamburgerIcon.propTypes = {
    open: PropTypes.bool
}

if (process.env.NODE_ENV !== 'production') {
    HamburgerIcon.displayName = 'HamburgerIcon'
}

export default HamburgerIcon
